import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JntConnectionResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";
import { invalidateSellerJntConnection } from "@/lib/query/core/invalidation";
import { resolveJntErrorMessage } from "@/modules/settings/lib/jntErrors";

type UseJntConnectionQueryParams = {
  storeSlug: string;
  enabled?: boolean;
};

export function useJntConnectionQuery({
  storeSlug,
  enabled = true,
}: UseJntConnectionQueryParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.jntConnection(storeSlug),
    // Store-scoped via the seller session cookie — storeSlug is in the query
    // key only for cache isolation, not the URL.
    queryFn: () =>
      apiJsonClient<JntConnectionResponse>(endpoints.seller.jntConnection),
    enabled: storeSlug.length > 0 && enabled,
  });

  return {
    connection: query.data?.connection ?? null,
    // Optimistic default while loading — consumers hide J&T when false.
    platformEnabled: query.data?.platformEnabled ?? true,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

type SaveJntCredentialsInput = {
  storeSlug: string;
  customerCode: string;
  password: string;
};

type MutationCallbacks = {
  onError?: (error: unknown) => void;
};

export function useSaveJntCredentialsMutation(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: SaveJntCredentialsInput) =>
      apiJsonClient<JntConnectionResponse>(endpoints.seller.jntConnection, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerCode: input.customerCode,
          password: input.password,
        }),
      }),
    onSuccess: async (_data, variables) => {
      await invalidateSellerJntConnection(queryClient, variables.storeSlug);
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveJntErrorMessage(mutation.error).message
      : null,
    reset: mutation.reset,
  };
}

type UpdateJntActiveInput = {
  storeSlug: string;
  isActive: boolean;
};

export function useUpdateJntActiveMutation(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: UpdateJntActiveInput) =>
      apiJsonClient<JntConnectionResponse>(
        endpoints.seller.jntConnectionActive,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isActive: input.isActive }),
        },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateSellerJntConnection(queryClient, variables.storeSlug);
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveJntErrorMessage(mutation.error).message
      : null,
  };
}

type DisconnectJntInput = {
  storeSlug: string;
};

export function useDisconnectJntMutation(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: DisconnectJntInput) => {
      void input;
      return apiJsonClient<JntConnectionResponse>(
        endpoints.seller.jntConnection,
        { method: "DELETE" },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateSellerJntConnection(queryClient, variables.storeSlug);
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveJntErrorMessage(mutation.error).message
      : null,
  };
}
